import React from 'react';
import { Link } from 'react-router-dom';

import { Callout } from './shared';

const Chat: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Chat Sessions &amp; Context</h2>
    <p className="text-base-content/50 text-sm mb-5">
      The single most important thing to understand about talking to MFlow&apos;s AI.
    </p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Every conversation lives in a <strong>session</strong>. A session has its own message history, and that history is
      the <em>only</em> context the agent has when it answers you in that session.
    </p>

    <Callout type="warning">
      <strong>Sessions are isolated.</strong> Nothing you say in one session is visible in any other — not a different
      project, not a different general chat, not even another session on the <em>same</em> project. If it isn&apos;t in
      this session&apos;s history, the agent doesn&apos;t know it.
    </Callout>

    <h3 className="font-semibold mt-6 mb-3">What&apos;s isolated from what</h3>
    <ul className="text-sm text-base-content/70 space-y-2 list-disc list-inside mb-6">
      <li>
        <strong>Two sessions on the same project</strong> don&apos;t share anything. Clicking{' '}
        <strong>New session</strong> starts a genuinely blank conversation, even though the project is identical to one
        you already discussed.
      </li>
      <li>
        <strong>A project session and a general chat</strong> are unrelated. Starting a{' '}
        <Link to="/docs/ai-chat" className="link link-primary">
          general chat
        </Link>{' '}
        doesn&apos;t carry over anything you asked in a project-scoped session, or vice versa.
      </li>
      <li>
        <strong>Two general chats</strong> are just as isolated from each other as two project sessions are.
      </li>
      <li>
        <strong>Attaching a project to a general chat</strong> only scopes messages sent <em>after</em> that point in{' '}
        <em>that</em> session. It doesn&apos;t retroactively pull in the project&apos;s history from elsewhere, and it
        doesn&apos;t affect any other session — that&apos;s why a divider marks exactly where the attachment happened in
        the conversation.
      </li>
    </ul>

    <h3 className="font-semibold mb-3">Continuing vs. starting fresh</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      If you want the agent to remember earlier context, <strong>resume the same session</strong> instead of opening a
      new one — use the session switcher at the top of the chat, or pick it from <strong>Recent sessions</strong> in the
      sidebar / the <strong>Chat</strong> page. <strong>New session</strong> is for when you actually want a clean
      slate, not a continuation.
    </p>

    <Callout type="tip">
      This isolation is deliberate, not a limitation to work around — it keeps one project&apos;s conversation from
      leaking into another&apos;s, and keeps a general chat from assuming context you never actually gave it. If an
      answer seems to be missing something you mentioned earlier, check whether that was in a different session first.
    </Callout>
  </div>
);

export default Chat;
